import axios from 'axios';
import Cookies from 'js-cookie';
import AxiosInterceptor from '../hooks/AxiosInterceptorHook';
import {apiURLs, getUseBillMixCannedResponse, getUseUpdateBillMixCannedResponse} from './Utility';
import BillMixCannedResponse from './BillMixCannedResponse';
import BillMixUpdateCannedResponse from './BillMixUpdateCannedResponse';

import { getCsrfToken as getStoredCsrfToken } from './csrfToken'

const csrfCookieNames = ['XSRF-TOKEN', 'CSRF-TOKEN', 'X-CSRF-TOKEN']

const getCsrfToken = () => {
  const storedToken = getStoredCsrfToken()
  if(storedToken) {
    return storedToken
  }
  for(let i = 0; i < csrfCookieNames.length; i += 1) {
    const token = Cookies.get(csrfCookieNames[i])
    if(token) {
      return token
    }
  }
  return null
}

const withCsrfHeader = (request = {}) => {
  const method = (request.method || 'GET').toUpperCase()
  if(!['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    return request
  }

  const csrfToken = getCsrfToken()
  if(!csrfToken) {
    return Promise.reject(new Error('CSRF token not found. Unable to send request.'))
  }

  return {
    ...request,
    headers: {
      ...(request.headers || {}),
      'X-CSRF-Token': csrfToken,
      'X-XSRF-TOKEN': csrfToken
    }
  }
}

// Singleton instance of TMBillMixAxios

let jwtToken = null;

const createTMBillMixAxiosInstance = () => {
  const axiosInstance = axios.create({
    baseURL: apiURLs.billMixBaseUrl,
    headers: {
      'Content-Type': 'application/json'
    },
    xsrfCookieName: 'XSRF-TOKEN',
    xsrfHeaderName: 'X-XSRF-TOKEN',
    withCredentials: true
  });

  axiosInstance.interceptors.request.use(withCsrfHeader);
  // Add interceptors if needed
  const interceptor = AxiosInterceptor(axiosInstance);

  return {axiosInstance, interceptor};
};

const TMBillMixAxiosInstance = createTMBillMixAxiosInstance();

function getMockedResponse(axios, method, path, data, statusCode) {
  const mock = new MockAdapter(axios);

  if(!method || !path || !statusCode) {
    throw new Error('Invalid parameters for getMockedResponse');
  }

  switch(method.toLowerCase()) {
    case 'put':
      mock.onPut(path).reply(statusCode || 200, data);
      break;
    case 'get':
      mock.onGet(path).reply(statusCode || 200, data);
      break;
    default:
      throw new Error(`Unsupported method ${method} for mockedUpdateResponse`);
  }

  return axios.put(path);
}

const TMBillMixAxios = {
  getMockedResponse,

  setJwtToken: jwtToken => {
    TMBillMixAxiosInstance.axiosInstance.defaults.headers.common.Authorization = `Bearer ${jwtToken}`;
  },

  getBillMix: async ({workstations}) => {
    // Use canned response if environment.useBillMixCannedResponse is true
    if(!!getUseBillMixCannedResponse && getUseBillMixCannedResponse()) {
      return getMockedResponse(axios, 'get', '/billmix', BillMixCannedResponse, 200);
    }
    const workstationFilter = workstations.join(',');
    return TMBillMixAxiosInstance.axiosInstance({
      method: 'GET',
      url: `/billmix`,
      params: {workstationIds: workstationFilter},
      timeout: 60000
    });
  },


  updateBillMix: async ({workstations, update_fastcash_billmix, update_withdrawal_billmix, callSigns, processingCenters, deployments}) => {

    if(!!getUseUpdateBillMixCannedResponse && getUseUpdateBillMixCannedResponse()) {
      return getMockedResponse(axios, 'put', '/billmix', BillMixUpdateCannedResponse, 200);
    }

    // Ensure required fields are set for each workstation
    const data = {
        workstations,
        update_fastcash_billmix,
        update_withdrawal_billmix
      };
      // console.log('Updating BillMix with data:', JSON.stringify(data, null, 2)); // Debug log
      // Keep request payload logging disabled to avoid exposing workstation/update details in client console logs.
    return await TMBillMixAxiosInstance.axiosInstance({
      method: 'PUT',
      url: `/billmix`,
      params: { callSigns: callSigns.join(','), processingCenters: processingCenters.join(','), deployments: deployments.join(',') },
      data
    });
  },
  setRequestInterceptor: (interceptor) => {
    TMBillMixAxiosInstance.interceptor.setRequestInterceptor(interceptor);
  },
  setResponseInterceptor: (interceptor) => {
    TMBillMixAxiosInstance.interceptor.setResponseInterceptor(interceptor);
  },

  ready: () => {
    return (
      TMBillMixAxiosInstance.axiosInstance &&
      TMBillMixAxiosInstance.axiosInstance.defaults &&
      TMBillMixAxiosInstance.axiosInstance.defaults.headers &&
      TMBillMixAxiosInstance.axiosInstance.defaults.headers.common &&
      TMBillMixAxiosInstance.axiosInstance.defaults.headers.common.Authorization &&
      true
    );
  }
}

export default TMBillMixAxios;

